define('viewModels/privileged/qm/policies/editors/SurveyInviteIntervalSelectorView',[
    'lib/knockout',
    'moment',
    'services/loggingService',
    'viewModels/ViewModel'
], function (
    ko,
    moment,
    loggingService,
    ViewModel
) {
    'use strict';
    return ViewModel.extend(function initIntervalSelectorView(inviteIntervalObservable) {
        var self = this;

        if (!ko.isObservable(inviteIntervalObservable)) {
            throw new Error('expected a knockout observable as first argument');
        }

        self.constraints = {
            repetitions: {
                min: 1,
                max: 30
            },
            periodDays: {
                min: 0
            },
            totalTimeDays: 90
        };

        self.inviteInterval = inviteIntervalObservable;

        /**
         * Generates an ISO-8601 repetition/period string given a number of repetitions and period in days
         * 
         * @param  {number} repetitions integer number of repetitions
         * @param  {number} periodDays integer number of days between repetitions
         * @returns {string} ISO-8601 formatted date string in the shape R{Int}/P{Int}D
         */
        self.generateInviteInterval = function generateInviteInterval(repetitions, periodDays) {
            var areInputsInvalid = isNaN(repetitions) || isNaN(periodDays);
            if (areInputsInvalid) {
                throw new Error('Repetitions and periodDays must both be numbers: ' +
                    JSON.stringify({ repetitions: repetitions, periodDays: periodDays }));
            }
            return "R" + Math.round(repetitions) + "/" + moment.duration(Math.round(periodDays), "day").toISOString();
        };

        /**
         * Validates that the provided string is an invite interval
         * 
         * @param  {string} value string to validate
         * @returns {boolean} is the provided value a valid survey invite interval
         */
        self.validateInterval = function validateInterval(value) {
            var INTERVAL_PATTERN = /^R[0-9]+\/P(?!$)(\d+Y)?(\d+M)?(\d+W)?(\d+D)?(T(?=\d+[HMS])(\d+H)?(\d+M)?(\d+S)?)?$/;
            return typeof value === 'string' && INTERVAL_PATTERN.test(value);
        };

        var initialInterval = self.inviteInterval();
        if (!self.validateInterval(initialInterval)) {
            // set default if failed validation
            self.inviteInterval(self.generateInviteInterval(self.constraints.repetitions.min, self.constraints.periodDays.min));
            if (typeof initialInterval === 'string' && initialInterval) {
                // log error if we failed validation on an existing interval string
                loggingService.error('survey invite interval string was malformed', {
                    interval: initialInterval
                });
            }
        }
    
        self.inviteIntervalRepetitions = self.computed({
            read: function () {
                var interval = self.inviteInterval();
                var intervalHasRepetitions = interval && interval.length && interval[0] === "R";
                if (!intervalHasRepetitions) { throw new Error('missing repetitions in interval string "' + interval + '"'); }
                var parts = interval.split('/');
                var repetitions = parts[0];
                var repetitionParts = repetitions.split('R');
                var numRepetitions = repetitionParts.length === 2 && !isNaN(Number(repetitionParts[1])) && Number(repetitionParts[1]);
                if (!numRepetitions) { throw new Error('malformed repetitions in interval string "' + interval + '"'); }
                return numRepetitions;
            },
            write: function (newValue) {
                var minPeriodDays = self.constraints.periodDays.min;
                var minRepetitions = self.constraints.repetitions.min;
                var newValueIsMin = newValue === minRepetitions;
                // set period days to min if new repetitions is min
                var currentPeriodDays = newValueIsMin ? minPeriodDays : self.inviteIntervalPeriodDays();
                var oldValue = self.inviteIntervalRepetitions();
                var didValueChange = newValue !== oldValue;
                if (didValueChange) {
                    var wouldBeInvalid = !newValueIsMin && currentPeriodDays === minPeriodDays;
                    var newPeriodDays = wouldBeInvalid ? minPeriodDays + 1 : currentPeriodDays;
                    var newInterval = self.generateInviteInterval(newValue, newPeriodDays);
                    self.inviteInterval(newInterval);
                }
            }
        });

        self.inviteIntervalPeriodDays = self.computed({
            read: function () {
                var interval = self.inviteInterval();
                var parts = interval ? interval.split("/") : [];
                var hasPeriod = parts.length === 2;
                if (!hasPeriod) { throw new Error('failed to parse period from interval string' + interval); }
                var period = parts[1];
                return moment.duration(period).asDays();
            },
            write: function (newValue) {
                var currentRepetitions = self.inviteIntervalRepetitions();
                var oldValue = self.inviteIntervalPeriodDays();
                var didValueChange = newValue !== oldValue;
                if (didValueChange) {
                    // periodDays can be 0 iff repetitions === 1
                    var wouldBeInvalid = currentRepetitions !== 1 && newValue === 0;
                    var newPeriodDays = wouldBeInvalid ? self.constraints.periodDays.min + 1 : newValue;
                    var newInterval = self.generateInviteInterval(currentRepetitions, newPeriodDays);
                    self.inviteInterval(newInterval);
                }
            }
        });

        self.totalTimeDays = self.computed(function () {
            return self.inviteIntervalPeriodDays() * (self.inviteIntervalRepetitions() - 1);
        }).extend({ validation: {
                validator: function (val) {
                    // total maximum days for the interval to span
                    // hack around to parameterize the i18n string
                    var isValid = val <= self.constraints.totalTimeDays;
                    if (!isValid) {
                        this.params = { maxDays: self.constraints.totalTimeDays };
                        this.message = 'policyResource.assignSurvey.inviteInterval.maxTotalTimeDays';
                    }
                    return isValid;
                }
            }
        });
    });
});
